import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import Pagination from "@/components/datasetManagement/Pagination";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import MultipleSearchWithSelect from "@/components/ui/MultipleSearchWithSelect";
import { NoDataMessage } from "@/components/ui/NoDataMessage";
import SearchWithSelect, {
  SearchFilterParams
} from "@/components/ui/searchWithSelect";
import { usePreviewGeom } from "@/hooks/map/usePreviewGeom";
import { normalizeFilterValue } from "@/lib/utils";
import { RootState } from "@/store";
import { clearSelectedDataset } from "@/store/slice/datasetSlice";
import { clearSelectedDatasetCatchment } from "@/store/slice/spatialSlice";
import { DatasetDataType } from "@/types/dataset";
import { generateFilterableAttributes } from "@/utils/dataset";
import {
  formatDatasetRows,
  generateColumnFilters,
  getTableState,
  includesStringFilter
} from "@/utils/table";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import { generateColumns } from "./columns";
import { DATASETS_SELECTED_NO_DATA_MESSAGE_MAPPING } from "./data";
import TableData from "./TableData";

type DatasetTabularModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isMultipleSearchTableDataset?: boolean;
};

export interface SearchFilterState {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
}

export default function DatasetTabularModal(
  props: Readonly<DatasetTabularModalProps>
) {
  const {
    isOpen = true,
    onClose,
    isMultipleSearchTableDataset = false
  } = props;

  const dispatch = useDispatch();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { selectedDataset, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const {
    catchmentSettingsTemp: { datasetConfig }
  } = useSelector((state: RootState) => state.spatial);

  const { data: userProfile } = useGetUserProfileAPI();

  const selectedCatchmentDataset = datasetConfig?.selectedDataset;
  const catchmentDatasets = datasetConfig?.datasets ?? {};

  const handleClose = () => {
    dispatch(clearSelectedDataset());
    dispatch(clearSelectedDatasetCatchment());
    onClose();
  };

  const rawDataset = selectedCatchmentDataset?.data?.id
    ? catchmentDatasets?.[selectedCatchmentDataset?.data.id ?? ""]?.rawDataset
    : rawDatasets[selectedDataset?.data.id ?? ""];

  const columns = rawDataset?.columns;

  const rows = useMemo(() => {
    return rawDataset?.rows || [];
  }, [rawDataset]);

  const data = useMemo(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    return rows.map((row) =>
      row.reduce<DatasetDetailRow>((acc, valueItem, index) => {
        if (columns[index] && valueItem !== null && valueItem !== undefined) {
          acc[normalizeFilterValue(columns[index].code)] = valueItem;
        }
        return acc;
      }, {})
    );
  }, [rows, columns]);

  const [filterState, setFilterState] = useState<SearchFilterState>({
    query: null,
    query_type: null,
    query_column: null
  });

  const columnFilters = useMemo(() => {
    return generateColumnFilters(filterState);
  }, [filterState]);

  const formattedData = useMemo(() => {
    return formatDatasetRows(
      data,
      (selectedDataset || selectedCatchmentDataset)?.categories || []
    );
  }, [data, selectedCatchmentDataset, selectedDataset]);

  /** Resolve catchment-first, mirroring how `rawDataset` is resolved above
   * (it keys off `selectedCatchmentDataset?.data?.id` first). When the table
   * is opened for a catchment dataset the selection lives in `state.spatial`
   * (selectedCatchmentDataset), not `state.dataset` — reading only
   * `selectedDataset` here left `selectedDatasetId` null, so "preview on map"
   * always failed the `!selectedDatasetId` guard with
   * MAP_VIEW_ACTION_COLUMN_FAILED. Catchment-first also keeps the preview's
   * datasetId/attributes in sync with the `rawDataset`/columns shown.
   */
  const activePreviewDataset = selectedCatchmentDataset || selectedDataset;

  const { handlePreviewClick, getPreviewDataFromRow } = usePreviewGeom({
    handleClose,
    selectedDatasetId: activePreviewDataset?.data.id,
    attributes: activePreviewDataset?.categories
  });

  const baseColumns = generateColumns({
    columnsConfig:
      (selectedDataset || selectedCatchmentDataset)?.categories || [],
    timezone: userProfile?.timezone,
    isShowActionMapPreviewEnabled: isMultipleSearchTableDataset,
    handlePreviewClick,
    getPreviewDataFromRow
  });

  // Create the table instance
  const table = useReactTable<DatasetDetailRow>({
    data: formattedData,
    columns: baseColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
        pageIndex: currentPage - 1
      },
      columnPinning: {
        right: ["action"]
      }
    },
    onSortingChange: (newSorting) => {
      setCurrentPage(1);
      setSorting(newSorting);
    },
    getSortedRowModel: getSortedRowModel(),
    filterFns: {
      includesString: includesStringFilter
    },
    state: getTableState(currentPage, pageSize, sorting, columnFilters)
  });

  const handleSingleFilterChange = (params: SearchFilterParams) => {
    const normalizedParams: SearchFilterState = {
      query: params.query ? [params.query] : null,
      query_type: params.query_type ? [params.query_type] : null,
      query_column: params.query_column ? [params.query_column] : null
    };

    setFilterState(normalizedParams);
    setCurrentPage(1);
  };

  const messageMapping = (rows?.length ?? 0) <= 0 ? "no-data" : "no-result";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <VisuallyHidden asChild>
        <DialogTitle />
      </VisuallyHidden>

      <DialogContent
        className="flex flex-col p-8 max-w-[calc(100vw-50px)] max-h-[calc(100vh-50px)] w-full h-full overflow-hidden"
        isHideClose
      >
        <div className="mb-3">
          <div className="flex flex-row items-start justify-between">
            <h1
              className="mb-1 text-xl font-semibold text-txtHeading"
              data-testid="modal-title"
            >
              {(selectedDataset || selectedCatchmentDataset)?.data
                .display_name || ""}
            </h1>
            <button
              type="button"
              className="p-0"
              onClick={handleClose}
              data-testid="close-button"
            >
              <CloseIcon />
            </button>
          </div>

          <p className="text-sm text-txtBody">
            {(selectedDataset || selectedCatchmentDataset)?.data
              .short_description || ""}
          </p>
        </div>
        {!isMultipleSearchTableDataset && (
          <SearchWithSelect<SearchFilterParams>
            options={generateFilterableAttributes(columns)}
            disabled={(rows?.length ?? 0) <= 0}
            onParamsChange={handleSingleFilterChange}
            searchParams={(() => {
              const state = filterState;
              return {
                query: state.query?.[0] ?? null,
                query_type: state.query_type?.[0] ?? null,
                query_column: state.query_column?.[0] ?? null
              };
            })()}
          />
        )}
        {isMultipleSearchTableDataset && (
          <MultipleSearchWithSelect
            options={generateFilterableAttributes(columns ?? [])}
            disabled={(rows?.length ?? 0) <= 0}
            onParamsChange={(params) => {
              setFilterState(params);
              setCurrentPage(1);
            }}
            searchParams={filterState}
          />
        )}
        <TableData<DatasetDetailRow>
          table={table}
          emptyDataMessage={
            <NoDataMessage
              message={
                DATASETS_SELECTED_NO_DATA_MESSAGE_MAPPING[messageMapping]
              }
            />
          }
        />

        <Pagination
          table={table}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalData={table.getFilteredRowModel().rows.length}
        />
      </DialogContent>
    </Dialog>
  );
}
